import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import type { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import type {
  PayrollRosterDraftMember,
  PayrollRosterMember,
} from "@/payroll/roster/types";
import {
  comparePayrollRosterValues,
  normalizePayrollRosterSortValue,
} from "@/payroll/roster/utils";
import { ROSTER_NAME_COLUMN_ID, ROSTER_NAME_COLUMN_LABEL } from "./constants";
import { rosterColumnHeader } from "./header";
import { RosterCellValue } from "./roster-cell-value";

interface NameColParams {
  editedIds: Set<string>;
  flaggedMembers: Set<PayrollRosterMember>;
  departedIds: Set<string>;
  onOpen: (draft: PayrollRosterDraftMember) => void;
  onDismissSuggestion: (draft: PayrollRosterDraftMember) => void;
  dismissingSuggestionId: string | null;
  busy: boolean;
  width: number;
}

const MISSING_ROSTER_REASON = "Active Volca user who is not on this roster";
const INACTIVE_SERVICE_TITAN_REASON =
  "No longer an active ServiceTitan technician. Remove them unless they should still be paid.";

export const NameCol = ({
  editedIds,
  flaggedMembers,
  departedIds,
  onOpen,
  onDismissSuggestion,
  dismissingSuggestionId,
  busy,
  width,
}: NameColParams): ColumnDef<PayrollRosterDraftMember> => ({
  id: ROSTER_NAME_COLUMN_ID,
  accessorFn: ({ member }) =>
    normalizePayrollRosterSortValue(member.display_name),
  header: rosterColumnHeader(ROSTER_NAME_COLUMN_LABEL),
  sortUndefined: "last",
  sortingFn: (left, right) =>
    comparePayrollRosterValues(
      left.original.member.display_name,
      right.original.member.display_name,
      "intrinsic_text",
    ),
  meta: { size: `${width}px` },
  cell: ({ row }) => {
    const { member, suggestion } = row.original;
    const isDeparted = departedIds.has(member.service_titan_employee_id);
    const statusDescription = suggestion
      ? MISSING_ROSTER_REASON
      : isDeparted
        ? INACTIVE_SERVICE_TITAN_REASON
        : undefined;
    const statusDescriptionId = statusDescription
      ? `roster-status-${row.id}`
      : undefined;
    const personName = member.display_name || "person";
    const dismissing =
      suggestion?.service_titan_employee_id === dismissingSuggestionId;

    return (
      <Stack
        direction="row"
        align="center"
        gap={2}
        className="max-w-full min-w-0"
      >
        <button
          type="button"
          aria-label={
            suggestion ? `Add ${personName} to roster` : `Edit ${personName}`
          }
          aria-describedby={statusDescriptionId}
          disabled={busy}
          className="min-w-0 flex-1 overflow-hidden rounded text-left focus-visible:ring-2 focus-visible:ring-focus focus-visible:outline-hidden"
          onClick={(event) => {
            event.stopPropagation();
            onOpen(row.original);
          }}
        >
          <Stack align="start" gap={0}>
            <RosterCellValue value={member.display_name || "-"} />
            {/* The ServiceTitan ID identifies the person as much as their name
                does, so it rides under it instead of taking a column. */}
            <Text
              variant="caption"
              tone="muted"
              className="truncate"
              title={member.service_titan_employee_id || "-"}
            >
              {member.service_titan_employee_id || "-"}
            </Text>
          </Stack>
        </button>
        {statusDescription && (
          <span id={statusDescriptionId} className="sr-only">
            {statusDescription}
          </span>
        )}
        {suggestion ? (
          <Stack direction="row" align="center" gap={1} className="shrink-0">
            {/* The reason rides the badge: spelled out inline it truncated on
                every row at every window size. */}
            <Tooltip>
              <TooltipTrigger
                render={<Badge variant="secondary" color="danger" size="sm" />}
              >
                Missing
              </TooltipTrigger>
              <TooltipContent>{MISSING_ROSTER_REASON}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    type="button"
                    variant="ghost"
                    color="danger"
                    size="icon-xs"
                    aria-label={`Mark ${personName} as not on payroll`}
                    loading={dismissing}
                    loadingLabel={`Mark ${personName} as not on payroll, in progress`}
                    disabled={busy || dismissing}
                    onClick={(event) => {
                      event.stopPropagation();
                      onDismissSuggestion(row.original);
                    }}
                  />
                }
              >
                <XIcon />
              </TooltipTrigger>
              <TooltipContent>Mark as not on payroll</TooltipContent>
            </Tooltip>
          </Stack>
        ) : isDeparted ? (
          // Not a blocker: ServiceTitan no longer lists this person as active,
          // so the roster recommends taking them off rather than refusing to
          // publish. Only the business knows if they are really gone.
          <Tooltip>
            <TooltipTrigger
              render={<Badge variant="secondary" color="warning" size="sm" />}
            >
              Inactive
            </TooltipTrigger>
            <TooltipContent>{INACTIVE_SERVICE_TITAN_REASON}</TooltipContent>
          </Tooltip>
        ) : flaggedMembers.has(member) ? (
          <Badge variant="secondary" color="warning" size="sm">
            Needs attention
          </Badge>
        ) : (
          editedIds.has(row.original.id) && (
            <Badge variant="secondary" size="sm">
              Edited
            </Badge>
          )
        )}
      </Stack>
    );
  },
});
